import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MessageSquareText, X } from 'lucide-react';

import { useAuth } from '../../../context/AuthContext';
import { getFeedbackSummary } from '../../../services/StudentFeedbackService';
import { subscribeToNotifications } from '../NotificationBell/notificationPulse';
import FeedbackList from './FeedbackList.jsx';
/* The student's widget shell, worn as it is: the same launcher in the
   same corner, the same panel, the same head. A trainer opening "what my
   students said" and a student opening "rate your classes" are the same
   gesture, and the app should not have two answers to it. */
import '../Student/Feedback/feedback-widget.css';
import './staff-feedback-widget.css';

/**
 * What students said — bottom right, trainers and administrators.
 *
 * Where the ratings live: reachable from whatever page someone happens
 * to be on, rather than from a tab on one dashboard. It replaced the
 * arrival cards that used to announce each rating as it landed -- they
 * polled on mount, so they opened themselves on sign-in. The launcher's
 * count says the same thing and waits to be asked.
 *
 * The rows come from FeedbackList, which reads them through the same
 * endpoint the full page does — so the scoping is the server's, not
 * this component's: a trainer gets their own sessions with the student
 * identity already stripped before it leaves the API, an administrator
 * gets every session with names. There is deliberately no prop here that
 * could ask for it the other way round.
 */

const StaffFeedbackWidget = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  /* The same ladder the student's widget climbs, in the same order
     (AdminCalendar ~252): rule out admin, then trainer. A permission
     answers "may you open this page"; it never answered "who are you". */
  const isSuperAdmin = user?.isSuperAdmin === true || user?.isSuperAdmin === 'true';
  const isAdmin = Boolean(isSuperAdmin || hasPermission('admin-calendar', 'view', 'lms'));
  const isTrainer = Boolean(
    !isAdmin
      && (hasPermission('trainer-calendar', 'view', 'lms')
        || hasPermission('trainer-calender', 'view', 'lms')),
  );
  const isStaff = isAdmin || isTrainer;

  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const loadingRef = useRef(false);

  /* The badge counts what is UNREVIEWED, not what exists: a number that
     only ever grows is a number people stop reading. The same figure the
     summary endpoint gives the dashboards, scoped to this caller. */
  const refresh = useCallback(async () => {
    if (!isStaff || loadingRef.current) return;
    loadingRef.current = true;
    try {
      setSummary(await getFeedbackSummary());
    } catch (error) {
      // An aside that cannot count is still an aside that opens.
      setSummary(null);
    } finally {
      loadingRef.current = false;
    }
  }, [isStaff]);

  useEffect(() => {
    if (!isStaff) return;
    refresh();
  }, [isStaff, refresh]);

  /* The bell already polls, so the count follows its pulse rather than a
     clock of its own — a rating that raised a notification has raised
     this too by the time anyone looks. */
  useEffect(() => {
    if (!isStaff) return undefined;
    return subscribeToNotifications((pulse) => {
      if (pulse.type === 'feedback_received' || pulse.type === 'unknown') refresh();
    });
  }, [isStaff, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      /* A feedback row opened from the list sits over this panel, and
         Escape belongs to whatever is on top: closing both at once would
         take away the thing being read AND the list it was read from.
         The same test FeedbackDetail applies to the waypoint above it. */
      if (document.querySelector('.pg-modal') || document.querySelector('.wp-modal')) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isStaff || !user) return null;

  const total = Number(summary?.total) || 0;
  const toReview = Number(summary?.pendingReviewCount) || 0;

  const subtitle = total === 0
    ? 'Nothing yet'
    : toReview > 0
      ? `${total} ${total === 1 ? 'rating' : 'ratings'} · ${toReview} to review`
      : `${total} ${total === 1 ? 'rating' : 'ratings'} · all reviewed`;

  return createPortal(
    <div className="fbw-root fbw-root--staff">
      {open && (
        <section
          className="fbw-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="sfw-title"
        >
          <header className="fbw-head">
            <span className="fbw-head-mark" aria-hidden="true">
              <MessageSquareText size={16} />
            </span>

            <div className="fbw-head-id">
              <h2 className="fbw-title" id="sfw-title">
                What students said
              </h2>
              <p className="fbw-sub">{subtitle}</p>
            </div>

            <button
              type="button"
              className="fbw-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div className="fbw-body">
            {/* The list the page and the dashboards render, in its short
                form. Names appear only on the rows that carry one, which
                is to say only for an administrator. */}
            <FeedbackList
              variant="widget"
              sub={isAdmin ? undefined : 'Names are not shown.'}
              onSeeAll={() => {
                setOpen(false);
                navigate('/session-feedback');
              }}
            />
          </div>
        </section>
      )}

      {/* The launcher, carrying what is waiting to be read. */}
      <button
        type="button"
        className={`fbw-fab${open ? ' is-open' : ''}`}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) refresh();
        }}
        aria-expanded={open}
        aria-label={
          toReview > 0
            ? `What students said, ${toReview} to review`
            : 'What students said'
        }
      >
        {open ? <X size={20} aria-hidden="true" /> : <MessageSquareText size={20} aria-hidden="true" />}
        {!open && toReview > 0 && (
          <span className="fbw-fab-count">{toReview > 9 ? '9+' : toReview}</span>
        )}
      </button>
    </div>,
    document.body,
  );
};

export default StaffFeedbackWidget;
